import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { StickyBottomBar } from '@/components/common/StickyBottomBar';
import { PRIMARY } from './detailStyles';

/*
 * 신청 버튼 상태 — 모바일 하단 고정 바와 PC 요약 카드가 같은 값을 쓴다.
 * apply: 신청하기(결제 모달 등 기존 흐름) / applied: 신청 완료·승인 대기 → 신청 내역 / disabled: 모집 전·모집 마감·정원 마감
 * disabled 에 onClick 이 있으면 회색이지만 누를 수 있다(누르면 막힌 이유 안내 — 예: 결제 설정 조회 실패)
 */
export interface ApplyActionState {
  mode: 'apply' | 'applied' | 'disabled';
  label: ReactNode;
  onClick?: () => void;
  busy?: boolean;
  /** 버튼 아래(모바일은 바 위쪽) 한 줄 안내 — 신청 완료 후 "링크·취소는 세미나 이력에서" */
  hint?: string;
}

const APPLY_BAR_HEIGHT_PX: number = 64;

/*
 * 공용 StickyBottomBar(sticky, 모바일에서 하단 탭 바로 위) 위에 높이 64px·좌우 16px 만 덧붙인다.
 * fixed 를 쓰면 당겨서 새로고침·스와이프 뒤로가기의 transform 에 같이 튄다 — 반드시 페이지 컨테이너의 마지막 자식으로 둔다.
 */
const Bar = styled(StickyBottomBar)`
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  column-gap: 12px;
  row-gap: 6px;
  min-height: ${APPLY_BAR_HEIGHT_PX}px;
  box-sizing: border-box;
  margin-top: 8px;
  border-top: none;

  @media (max-width: 768px) {
    padding: 8px 16px;
  }
`;

const PriceBox = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const PriceCaption = styled.small`
  font-size: 11px;
  line-height: 1.2;
  color: #6b7280;
`;

const PriceValue = styled.b`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
  color: #111827;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const actionBackground = (mode: ApplyActionState['mode']): string =>
  mode === 'apply' ? PRIMARY : mode === 'applied' ? '#9ca3af' : '#d1d5db';

const BarButton = styled.button<{ actionMode: ApplyActionState['mode'] }>`
  flex-shrink: 0;
  width: 150px;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: ${({ actionMode }) => actionBackground(actionMode)};
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
  cursor: ${({ actionMode }) => (actionMode === 'disabled' ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ApplyBottomBarProps {
  priceText: string;
  action: ApplyActionState;
  /** 쿠폰 적용가가 있으면 "쿠폰 적용가" + 할인율 */
  couponRate?: number | null;
  /** 가격 위 작은 글자(기본 "참가비") — 결제했으면 "결제 금액" */
  caption?: string;
}

/** 바 맨 위 한 줄 안내(폭 전체) */
const BarHint = styled.p`
  flex-basis: 100%;
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #4b5563;
  word-break: keep-all;
`;

const RateText = styled.span`
  margin-right: 4px;
  color: #e11d48;
`;

/** 모바일 하단 바 — 왼쪽 참가비(쿠폰 있으면 쿠폰 적용가), 오른쪽 신청하기 150×48. 페이지 컨테이너의 마지막 자식으로 둘 것 */
export const ApplyBottomBar: React.FC<ApplyBottomBarProps> = ({ priceText, action, couponRate, caption }) => (
  <Bar>
    {action.hint && <BarHint>{action.hint}</BarHint>}
    <PriceBox>
      <PriceCaption>{caption ?? (couponRate != null ? '쿠폰 적용가' : '참가비')}</PriceCaption>
      <PriceValue>
        {couponRate != null && couponRate > 0 && <RateText>{couponRate}%</RateText>}
        {priceText}
      </PriceValue>
    </PriceBox>
    <BarButton
      type="button"
      actionMode={action.mode}
      disabled={(action.mode === 'disabled' && !action.onClick) || action.busy}
      onClick={action.onClick}
    >
      {action.label}
    </BarButton>
  </Bar>
);
